import { DISPLAY_STATUS, type DisplayStatusType } from '@constants'
import { Grid } from '@fortune-ui'
import { type WpOrderStatus, type WpOrderType, ORIGINAL_ORDER_STATUS, WP_ORDER_STATUS, WP_ORDER_TYPE } from '@hooks-api/types/dataModel/eCommerceOrders'
import { HStack } from '@pets-ui/HStack/HStack'
import { QuestionOutlined } from '@pets-ui/Icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { Tooltip } from '@pets-ui/Tooltip/Tooltip'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { formatPrice } from '@utils'
import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useContextWpMyAccount } from '../useContextWpMyAccount'
import { type OrderListDTO } from '../utils/transformOrderDataDTO'
import { OrderLabel } from './OrderLabel'

interface WarehouseOrderReceiptProps {
  isProcessingOrder: boolean
  orderId: string
  quantity: number
  remainQuantity?: number
  orderCreatedTime: string
  currency: string
  total: number
  remainTimes: number
}

const OrderReceiptContainer = ({ children }: { children: ReactNode }) => {
  const isWordPressTablet = useResponsive('wordPressTablet')
  return (
    isWordPressTablet
      ? (
        <Grid container rowSpacing={1} wrap="nowrap">
          <Grid xs={12} container item rowSpacing={0.5} columnSpacing={1}>
            {children}
          </Grid>
        </Grid>
      )
      : (
        <Grid container item rowSpacing={0.5} columnSpacing={1}>
          {children}
        </Grid>
      )
  )
}

export const WarehouseOrderReceipt = (props: WarehouseOrderReceiptProps) => {
  const { t } = useTranslation()
  const { orderId, quantity, remainQuantity, currency, total, orderCreatedTime, isProcessingOrder, remainTimes } = props
  return (
    <OrderReceiptContainer>
      <OrderLabel label={t('wpOrder.orderId')} value={orderId} />
      <OrderLabel label={t('wpOrder.orderCreateTime')} value={orderCreatedTime} />
      <OrderLabel label={t('wpOrder.purchaseQuantity')} value={quantity} />
      <OrderLabel label={t('wpOrder.remainQuantity')} value={isProcessingOrder ? `${remainQuantity}(${t('wpMyAccount.remainDeliveryAmount')}：${t('wpMyAccount.deliveryRemainTimes', { remainTimes })})` : '-'} />
      <OrderLabel bold label={t('wpOrder.orderTotal')} value={formatPrice(currency, total)} />
    </OrderReceiptContainer>
  )
}

interface SubscriptionOrderReceiptProps {
  orderId: string
  quantity: number
  orderCreatedTime: string
  periodValue?: number
  periodUnit: string
  currency: string
  subscriptionTotal?: number
}
export const SubscriptionOrderReceipt = (props: SubscriptionOrderReceiptProps) => {
  const { t } = useTranslation()
  const { orderId, quantity, currency, subscriptionTotal, orderCreatedTime, periodValue, periodUnit } = props
  return (
    <OrderReceiptContainer>
      <OrderLabel label={t('wpOrder.orderId')} value={orderId} />
      <OrderLabel label={t('wpOrder.orderCreateTime')} value={orderCreatedTime} />
      <OrderLabel label={t('wpSubscription.delivery')} value={t('wpSubscription.deliveryValue', { periodValue, periodUnit })} />
      <OrderLabel label={t('wpOrder.purchaseQuantity')} value={quantity} />
      <OrderLabel bold label={t('wpOrder.orderTotal')} value={t('wpSubscription.paymentValue', { amount: formatPrice(currency, subscriptionTotal), periodValue, periodUnit })} />
    </OrderReceiptContainer>
  )
}

interface FastPassOrderReceiptProps {
  orderId: string
  transactionId?: string | null
  quantity: number
  orderCreatedTime: string
  currency: string
  total: number
  displayStatus?: DisplayStatusType
}
export const FastPassOrderReceipt = (props: FastPassOrderReceiptProps) => {
  const { t } = useTranslation()
  const isWordPressTablet = useResponsive('wordPressTablet')
  const { environment } = useContextWpMyAccount()
  const isMerchantEnvironment = environment === 'merchant'
  const { orderId, quantity, orderCreatedTime, transactionId, currency, total, displayStatus } = props
  return (
    <OrderReceiptContainer>
      <OrderLabel label="交易編號" value={transactionId} />
      <OrderLabel label={t('wpOrder.orderId')} value={orderId} childrenBelow={!isWordPressTablet}>
        <HStack gap={0.5} alignItems="center">
          {displayStatus && (
            <Text component="span" color={colors.grey800}>
              (
              {t(`orderDetail.displayStatus.${displayStatus}`, { defaultValue: displayStatus })}
              )
            </Text>
          )}
          {displayStatus && displayStatus === DISPLAY_STATUS.PREPARING_SHIPMENT && (
            <Tooltip
              placement="bottom"
              variant="dark"
              PaperProps={{ maxWidth: 180 }}
              title={(
                <Text>
                  {isMerchantEnvironment
                    ? t('orderDetail.readyForShipment2B', { defaultValue: '準備出貨：商品正在進行包裝，此階段已無法取消訂單，如需協助請聯繫業務。' })
                    : t('orderDetail.readyForShipment2C', { defaultValue: '準備出貨：商品正在進行包裝，此階段已無法取消訂單。' })}
                </Text>
              )}
            >
              <QuestionOutlined color={colors.grey600} fontSize={12} />
            </Tooltip>
          )}
        </HStack>
      </OrderLabel>
      <OrderLabel label={t('wpOrder.orderCreateTime')} value={orderCreatedTime} />
      <OrderLabel label={t('wpOrder.quantity')} value={quantity} />
      <OrderLabel bold label={t('wpOrder.orderTotal')} value={formatPrice(currency, total)} />
    </OrderReceiptContainer>
  )
}

interface NormalOrderReceiptProps {
  orderId: string
  quantity: number
  orderCreatedTime: string
  currency: string
  total: number
  displayStatus?: DisplayStatusType
}
export const NormalOrderReceipt = (props: NormalOrderReceiptProps) => {
  const { t } = useTranslation()
  const isWordPressTablet = useResponsive('wordPressTablet')
  const { orderId, quantity, orderCreatedTime, currency, total, displayStatus } = props
  return (
    <OrderReceiptContainer>
      <OrderLabel label={t('wpOrder.orderId')} value={orderId} childrenBelow={!isWordPressTablet}>
        <HStack gap={0.5} alignItems="center">
          {displayStatus && (
            <Text component="span" color={colors.grey800}>
              (
              {t(`orderDetail.displayStatus.${displayStatus}`, { defaultValue: displayStatus })}
              )
            </Text>
          )}
          {displayStatus && displayStatus === DISPLAY_STATUS.PREPARING_SHIPMENT && (
            <Tooltip
              placement="bottom"
              variant="dark"
              PaperProps={{ maxWidth: 180 }}
              title={(
                <Text>
                  {t('orderDetail.readyForShipment2C', { defaultValue: '準備出貨：商品正在進行包裝，此階段已無法取消訂單。' })}
                </Text>
              )}
            >
              <QuestionOutlined color={colors.grey600} fontSize={12} />
            </Tooltip>
          )}
        </HStack>
      </OrderLabel>
      <OrderLabel label={t('wpOrder.orderCreateTime')} value={orderCreatedTime} />
      <OrderLabel label={t('wpOrder.quantity')} value={quantity} />
      <OrderLabel bold label={t('wpOrder.orderTotal')} value={formatPrice(currency, total)} />
    </OrderReceiptContainer>
  )
}

interface OrderReceiptProps {
  order: OrderListDTO
  type: WpOrderType
  status: WpOrderStatus
}

export function OrderReceipt({ order, type, status }: OrderReceiptProps) {
  const { t } = useTranslation()
  const {
    orderCreatedTime,
    orderId,
    quantity,
    total,
    remainQuantity,
    currency,
    transactionId,
    relatedOrders,
    periodUnit,
    periodValue,
    subscriptionTotal,
    originalStatus,
    displayStatus,
  } = order
  const isWarehouseProcessingOrder = status === WP_ORDER_STATUS.PROCESSING || originalStatus === ORIGINAL_ORDER_STATUS.COMPLETED
  const remainTimes = relatedOrders?.filter(order => order.status === WP_ORDER_STATUS.UNCREATED).length ?? 0

  if (type === WP_ORDER_TYPE.NORMAL) {
    return (
      <NormalOrderReceipt
        orderId={orderId}
        quantity={quantity}
        orderCreatedTime={orderCreatedTime}
        currency={currency}
        total={total}
        displayStatus={displayStatus}
      />
    )
  }
  if (type === WP_ORDER_TYPE.SERVICE_ORDER) {
    return (
      <WarehouseOrderReceipt
        isProcessingOrder={isWarehouseProcessingOrder}
        orderId={orderId}
        quantity={quantity}
        remainQuantity={remainQuantity}
        orderCreatedTime={orderCreatedTime}
        currency={currency}
        total={total}
        remainTimes={remainTimes}
      />
    )
  }
  if (type === WP_ORDER_TYPE.SUBSCRIBE) {
    return (
      <SubscriptionOrderReceipt
        orderId={orderId}
        quantity={quantity}
        orderCreatedTime={orderCreatedTime}
        periodValue={periodValue}
        periodUnit={t(`wpMyAccount.${periodUnit}`)}
        currency={currency}
        subscriptionTotal={subscriptionTotal}
      />
    )
  }
  if (type === WP_ORDER_TYPE.PAYMENT_TRANSACTION) {
    return (
      <FastPassOrderReceipt
        orderId={orderId}
        quantity={quantity}
        orderCreatedTime={orderCreatedTime}
        transactionId={transactionId}
        currency={currency}
        total={total}
        displayStatus={displayStatus}
      />
    )
  }
  if (type === WP_ORDER_TYPE.CAMPAIGN) {
    return (
      <FastPassOrderReceipt
        orderId={orderId}
        quantity={quantity}
        orderCreatedTime={orderCreatedTime}
        transactionId={transactionId}
        currency={currency}
        total={total}
        displayStatus={displayStatus}
      />
    )
  }
  return null
}
